import React, { useMemo } from "react";
import {
  Autocomplete,
  TextField,
  Grid,
  FormControl,
  Box,
  Typography,
  useTheme,
} from "@mui/material";

const ALL_FUNDS_OPTION = { name: "All funds", abbreviation: "ALL" };

const PortfolioFilters = ({
  fundVal,
  StrategeyVal,
  StrategeyTypename,
  fundOptions,
  strategyOptions,
  StrategyType,
  isStrategist,
  disableStrategyButton,
  StrategyName,
  handleFundChange,
  handleStrategyChange,
  handleSideChange,
}) => {
  const theme = useTheme();

  const selectedFund = useMemo(() => {
    if (!Array.isArray(fundOptions) || fundOptions.length === 0) {
      return ALL_FUNDS_OPTION;
    }
    return (
      fundOptions.find((fund) => fund.abbreviation === fundVal) ||
      ALL_FUNDS_OPTION
    );
  }, [fundOptions, fundVal]);

  return (
    <Grid container spacing={2} alignItems="flex-end">
      {!isStrategist && (
        <Grid size={{ xs: 2.2}}>
          <FormControl fullWidth>
            <Autocomplete
              disableClearable
              value={selectedFund}
              options={fundOptions}
              getOptionLabel={(option) =>
                option?.name || option?.abbreviation || ""
              }
              isOptionEqualToValue={(option, value) =>
                option?.abbreviation === value?.abbreviation
              }
              filterOptions={(options, { inputValue }) => {
                const query = inputValue.trim().toLowerCase();
                if (!query) return options;
                return options.filter(
                  (fund) =>
                    fund.abbreviation === "ALL" ||
                    String(fund.abbreviation || "")
                      .toLowerCase()
                      .includes(query) ||
                    String(fund.name || "")
                      .toLowerCase()
                      .includes(query)
                );
              }}
              slotProps={{
                popper: {
                  sx: { zoom: 0.9, width: 280 },
                },
                paper: {
                  sx: { width: 280, maxWidth: 280 },
                },
                listbox: {
                  sx: {
                    py: 0.25,
                    fontSize: 13,
                    "& .MuiAutocomplete-option": {
                      minHeight: "unset",
                      py: "4px",
                      px: 1,
                      alignItems: "flex-start",
                      fontWeight: 400,
                      borderBottom: `1px solid ${theme.palette.divider}`,
                      "&:last-of-type": {
                        borderBottom: "none",
                      },
                      "& .fund-option-name": {
                        fontWeight: "700 !important",
                      },
                    },
                  },
                },
              }}
              sx={{
                "& .MuiAutocomplete-input, & .MuiInputLabel-root": {
                  fontSize: 12,
                },
              }}
              onChange={(event, newValue) => {
                handleFundChange(event, newValue?.abbreviation ?? "ALL");
              }}
              renderOption={(props, option) => {
                const { key, ...optionProps } = props;
                const isAll = option.abbreviation === "ALL";
                return (
                  <Box
                    component="li"
                    key={key}
                    {...optionProps}
                    sx={{ display: "block !important", minWidth: 0 }}
                  >
                    <Box
                      className="fund-option-name"
                      component="span"
                      sx={{
                        display: "block",
                        fontSize: 12,
                        lineHeight: 1.25,
                        fontWeight: "700 !important",
                        color: theme.palette.lightGrey.textColor,
                        whiteSpace: "nowrap",
                      }}
                    >
                      {option.name || option.abbreviation}
                    </Box>
                    {!isAll &&
                      option.abbreviation &&
                      option.name !== option.abbreviation && (
                        <Typography
                          sx={{
                            fontSize: 12,
                            lineHeight: 1.25,
                            color: theme.palette.text.secondary,
                            whiteSpace: "nowrap",
                          }}
                        >
                          {option.abbreviation}
                        </Typography>
                      )}
                  </Box>
                );
              }}
              renderInput={(params) => (
                <TextField {...params} variant="standard" label="Funds" />
              )}
            />
          </FormControl>
        </Grid>
      )}
      <Grid size={{ xs: 1.5 }}>
        <FormControl fullWidth>
          <Autocomplete
            disableClearable
            value={StrategeyVal}
            ListboxProps={{
              sx: { fontSize: 12 },
            }}
            sx={{
              "& .MuiAutocomplete-input, & .MuiInputLabel-root": {
                fontSize: 12,
              },
            }}
            onChange={handleStrategyChange}
            disabled={
              isStrategist
                ? false
                : disableStrategyButton || fundVal === "" || !StrategyName || StrategyName?.length < 1
            }
            options={strategyOptions}
            renderInput={(params) => (
              <TextField {...params} variant="standard" value={StrategeyVal} label="Strategy" />
            )}
          />
        </FormControl>
      </Grid>
      <Grid size={{ xs: 1.5 }}>
        <FormControl fullWidth>
          <Autocomplete
            disableClearable
            value={StrategeyTypename}
            ListboxProps={{
              sx: { fontSize: 12 },
            }}
            sx={{
              "& .MuiAutocomplete-input, & .MuiInputLabel-root": {
                fontSize: 12,
              },
            }}
            onChange={handleSideChange}
            disabled={
              isStrategist
                ? false
                : disableStrategyButton || fundVal === "" || !StrategyName || StrategyName?.length < 1
            }
            options={StrategyType}
            renderInput={(params) => (
              <TextField {...params} variant="standard" value={StrategeyTypename} label="Side" />
            )}
          />
        </FormControl>
      </Grid>
    </Grid>
  );
};

export default React.memo(PortfolioFilters);
